// Reading `continueChat`'s refusal bodies.
//
// Spec: docs/superpowers/specs/2026-09-10-continuechat-turn-bounds-design.md
// ("Response contract and the client change").
//
// The bounds added for audit Finding 2 gave `continueChat` two refusals that a
// learner can actually hit while doing nothing wrong: a 429 when they send
// turns faster than the per-minute bucket refills, and a 409 when a single
// conversation reaches its turn ceiling. Both answer with the JSON shape
// `lessonsApi` already uses, `{"error": <code>, "retryable": <bool>}`, and the
// 429 adds `retryAfterSeconds`. Every OTHER failure on this endpoint is still a
// bare string body (`"forbidden"`, `"Invalid action"`), and one of those bare
// strings is load-bearing: `CourseSetupWizard.submitSelections` recognises its
// benign "Dialog is already complete" 400 by matching the text.
//
// Hence the two halves of this module.
//
// `readRefusal` exists because a `fetch` body can be read exactly once. Reading
// it as JSON would consume it and leave the wizard's text match with nothing to
// match, so it reads the body once as text, and hands back BOTH the parsed
// `error` code (null when the body is not JSON, which is how a caller tells a
// new refusal from every pre-existing one) and the raw text. Nothing here
// throws: a body that cannot be read at all, on a connection that died
// mid-response, is reported as no code and an empty string, which is exactly
// the pre-existing generic-failure case every call site already handles.
//
// `refusalMessageKey` is the two-entry map from a code to the locale key whose
// copy tells the learner what happened. It returns null for anything else,
// INCLUDING a refusal code this build has never heard of, so a call site can
// always tell "show my specific copy" from "fall back to my existing toast".
// `src/scripts/friendlyError.js` was considered for this map and rejected: its
// lookup is hard-wired to the auth domain, its entry points take an error
// object rather than a code, and it cannot answer "unmapped" -- an unknown chat
// code would come back as `errors.auth.generic`, which is worse than the
// generic toast it would replace.
//
// The two code strings below are pinned against the backend constants in
// `functions/source/helpers/turnRefusal.py` by
// `functions/testing/ConstantsSyncTest.py::testTurnRefusalCodesArePinned`, so a
// rename on either side fails a test rather than silently stranding the shipped
// client on a code the server no longer sends.

// Refusal code -> locale key. Exactly the two codes the backend's
// `turnRefusal.py` defines; anything else is deliberately absent.
const REFUSAL_MESSAGE_KEYS = {
  rateLimited: "errors.chat.rateLimited",
  turnLimitReached: "errors.chat.turnLimitReached",
};

/**
 * Read a non-OK `continueChat` response's body ONCE.
 *
 * @param {Response} response a response whose `ok` is false
 * @returns {Promise<{code: string|null, text: string}>} `code` is the `error`
 *   field when the body parses as a JSON object carrying a string `error`, and
 *   null otherwise; `text` is the raw body, or "" when it could not be read.
 */
export async function readRefusal(response) {
  let text = "";
  try {
    text = await response.text();
  } catch (error) {
    // A body that cannot be read (aborted response, already-consumed stream)
    // is not an error of its own: the caller's generic failure path is right.
    return { code: null, text: "" };
  }

  let code = null;
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed.error === "string") {
      code = parsed.error;
    }
  } catch (error) {
    // Not JSON. Every pre-existing refusal on this endpoint lands here.
  }
  return { code, text };
}

/**
 * The locale key for a refusal code, or null when this build has no specific
 * copy for it and the caller should keep its existing message.
 *
 * @param {string|null} code the `error` field `readRefusal` returned
 * @returns {string|null}
 */
export function refusalMessageKey(code) {
  if (!code) return null;
  return Object.prototype.hasOwnProperty.call(REFUSAL_MESSAGE_KEYS, code)
    ? REFUSAL_MESSAGE_KEYS[code]
    : null;
}

/**
 * True when this refusal is the shared per-minute rate limit.
 *
 * Two call sites want that one refusal and no other, because the turn
 * ceiling's copy tells the learner to start a new conversation and neither of
 * those screens offers a way to start a second course-background dialog:
 * `LessonsView.handleWizardChatInstead` and `CourseSetupWizard.submitSelections`.
 * They ask here rather than comparing against a `"rateLimited"` literal of
 * their own, so the cross-file pin in `ConstantsSyncTest` covers them too. A
 * literal at the call site would keep matching a code the backend had renamed
 * out from under it, and the toast would silently fall back to generic copy.
 *
 * @param {string|null} code the `error` field `readRefusal` returned
 * @returns {boolean}
 */
export function isRateLimited(code) {
  return (
    code != null && refusalMessageKey(code) === REFUSAL_MESSAGE_KEYS.rateLimited
  );
}
